/** Port von `Client/Pages/Views/IwmViewBestellposition.razor`. */

import { orderPositionApi } from '@/api/services/orderPositionApi';
import { orderPositionEditTemplates } from '@/components/columns/edit/orderPositionEditColumns';
import { orderPositionCellTemplates } from '@/components/columns/orderPositionColumns';
import { OrderPositionDivideDialog } from '@/components/dialogs/OrderPositionDivideDialog';
import { DataGrid } from '@/components/grid/DataGrid';
import { ContainerSidebar } from '@/components/sidebars/ContainerSidebar';
import { OrderPositionContainerSidebarProvider } from '@/components/sidebars/OrderPositionContainerSidebarContext';
import { useSidebar } from '@/components/sidebars/useSidebar';
import { Button } from '@/components/ui';
import { AuditButton } from '@/components/views/AuditButton';
import { DeleteButton } from '@/components/views/DeleteButton';
import { ExcelExportButton } from '@/components/views/ExcelExportButton';
import { MessageDialog } from '@/components/views/MessageDialog';
import { NewButton } from '@/components/views/NewButton';
import { ViewAuditSidebar } from '@/components/views/ViewAuditSidebar';
import { ViewPageFrame } from '@/components/views/ViewPageFrame';
import { firstSelected } from '@/components/views/firstSelected';
import { useContainerPermissions } from '@/components/views/useContainerPermissions';
import { useMessageDialog } from '@/components/views/useMessageDialog';
import { useViewIdParam } from '@/components/views/useViewIdParam';
import { useViewPage } from '@/hooks/useViewPage';
import type { OrderPosition } from '@/types/entities';
import { EntityTypes } from '@/types/enums';

import {
  buildRecalcSuccessText,
  RECALC_CAPTION,
  RECALC_FAILED,
  RECALC_FAILED_CAPTION,
  RECALC_NO_CONTAINER,
  RECALC_RUNNING,
} from './orderPositionRecalcText';

export function OrderPositionView() {
  const viewId = useViewIdParam();
  const container = useContainerPermissions();
  const page = useViewPage<OrderPosition>({
    viewId,
    entity: EntityTypes.Bestellposition,
    api: orderPositionApi,
    cellTemplates: orderPositionCellTemplates,
    editTemplates: orderPositionEditTemplates,
    afterRefresh: container.load,
  });
  const { permissions, selectedId, view } = page;
  const message = useMessageDialog();
  const refreshGrid = (): void => void page.gridRef.current?.refresh();

  const auditSidebar = useSidebar();
  const containerSidebar = useSidebar({ onHide: refreshGrid });
  // `IwmBaseDialog` und `IwmBaseSidebar` teilen den Vertrag `Visible`/`Show()`/`Hide()`.
  const divideDialog = useSidebar();

  // Port von `OnSave` — hier zusätzlich mit Fehlermeldung.
  const saveCell = async (item: OrderPosition, columnName: string): Promise<void> => {
    try {
      await page.saveCell(item, columnName);
    } catch (error) {
      message.show('Save fehlgeschlagen', `Es ist ein Fehler aufgetreten. ${String(error)}`);
    }
  };

  // Port von `Recalc()`; die Reihenfolge der Zuweisungen bleibt erhalten.
  const recalc = async (): Promise<void> => {
    try {
      message.showText(RECALC_RUNNING);
      const model = firstSelected(page.gridRef.current?.getSelectedRecords() ?? []);
      if ((model.ContainerId ?? 0) <= 0) {
        message.show(RECALC_CAPTION, RECALC_NO_CONTAINER);
        return;
      }
      const result = await orderPositionApi.reCalcContainer(viewId, model);
      page.gridRef.current?.setRowData(result.Id, result);
      message.show(RECALC_CAPTION, buildRecalcSuccessText(model));
    } catch {
      message.show(RECALC_FAILED_CAPTION, RECALC_FAILED);
    }
  };

  // Port von `Divide()`.
  const divide = (): void => {
    const records = page.gridRef.current?.getSelectedRecords() ?? [];
    if (records.length !== 1) {
      message.show('OrderPosition aufteilen', 'Bitte wählen Sie einen Datensatz.');
      return;
    }
    divideDialog.show();
  };

  const toolbar = (
    <>
      {container.permissions?.AllowCreate === true ? (
        <NewButton
          title="New Container"
          onClick={() => {
            containerSidebar.show();
          }}
        >
          New Container
        </NewButton>
      ) : null}
      {permissions?.AllowAudit === true ? (
        <AuditButton
          selectedId={selectedId}
          onClick={() => {
            auditSidebar.show();
          }}
        />
      ) : null}
      {view?.AdditionalFunctions?.includes('Frachtkosten') === true ? (
        <Button className="button-c" disabled={selectedId === null} onClick={() => recalc()}>
          Calculate Freight Charges
        </Button>
      ) : null}
      {view?.AdditionalFunctions?.includes('Aufteilen') === true ? (
        <Button className="button-c" disabled={selectedId === null} onClick={divide}>
          Divide Positions
        </Button>
      ) : null}
      {permissions?.AllowDelete === true ? (
        <DeleteButton selectedId={selectedId} onClick={() => page.deleteSelected()} />
      ) : null}
      <ExcelExportButton running={page.excelExportRunning} onClick={() => page.exportExcel()} />
    </>
  );

  // Die Bearbeitungs-Templates der `Container_*`-Spalten öffnen die Seitenleiste über den
  // Provider; er muss deshalb die gesamte Seite umschließen.
  return (
    <OrderPositionContainerSidebarProvider
      position="Right"
      target=".view-page"
      onHide={refreshGrid}
    >
      <MessageDialog
        open={message.open}
        onClose={message.close}
        caption={message.caption}
        text={message.text}
        width="450px"
      />
      <OrderPositionDivideDialog
        open={divideDialog.visible}
        onClose={() => {
          divideDialog.hide();
        }}
        viewId={viewId}
        orderPositionId={selectedId ?? 0}
        onSuccess={refreshGrid}
      />

      <ViewPageFrame view={view} gridClassName="view-grid-container" toolbar={toolbar}>
        <DataGrid<OrderPosition>
          ref={page.gridRef}
          id={`grid-bestellposition-${view?.ViewName ?? ''}`}
          columns={page.columns}
          view={view}
          dataSource={page.dataSource}
          onCellSave={(item, columnName) => saveCell(item, columnName)}
          onSelectedIdChange={(id) => {
            page.setSelectedId(id);
          }}
        />
      </ViewPageFrame>

      {permissions?.AllowAudit === true ? (
        <ViewAuditSidebar
          entity={EntityTypes.Bestellposition}
          viewId={viewId}
          selectedId={selectedId}
          open={auditSidebar.visible}
          onClose={() => {
            auditSidebar.hide();
          }}
        />
      ) : null}
      {container.permissions?.AllowCreate === true ? (
        <ContainerSidebar
          open={containerSidebar.visible}
          position="Right"
          target=".view-page"
          onHide={() => {
            containerSidebar.hide();
          }}
        />
      ) : null}
    </OrderPositionContainerSidebarProvider>
  );
}

export default OrderPositionView;
